<template>
  <ehc-page v-if="userHasPermission(['superAdmin'])">
    <ehc-header
      text="Property Templates"
      :actions="headerActions"
      @refreshTemplates="loadTemplates"
      @openCreateTemplate="openCreateTemplateDialog"
    ></ehc-header>

    <ehc-sheet>
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        class="mb-3"
      ></v-progress-linear>

      <v-alert v-if="error" type="error" outlined dense class="mb-3">
        {{ error }}
      </v-alert>

      <div class="d-flex justify-end mb-3">
        <v-btn-toggle v-model="view" mandatory dense color="primary">
          <v-btn value="gallery" small outlined>
            <v-icon small left>mdi-view-grid-outline</v-icon>
            Cards
          </v-btn>
          <v-btn value="table" small outlined>
            <v-icon small left>mdi-view-list-outline</v-icon>
            List
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-row v-if="!loading && view === 'gallery'">
        <v-col
          v-for="template in templateCards"
          :key="template.propertyId"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="2"
        >
          <ehc-property-card
            :property="template"
            :actions="cardActions"
            @editProperty="editTemplate"
            @deleteProperty="confirmDeleteTemplate"
            @publishProperty="publishTemplate"
            @propertyLink="showTemplateLink"
            @propertyQRCode="showTemplateQRCode"
            @downloadPropertyPdf="downloadPropertyGuidePdf"
            @goToFirestoreProperty="goToFirestoreProperty"
            @syncPropertyCardView="syncPropertyCardViewFromProperty"
          ></ehc-property-card>
          <div class="mt-2">
            <v-chip v-if="template.type" small color="primary" outlined>
              {{ template.type }}
            </v-chip>
            <span v-else class="grey--text text-caption">no property type</span>
          </div>
        </v-col>
        <v-col v-if="!templateCards.length" cols="12">
          <p class="text-center grey--text py-6 mb-0">No templates found.</p>
        </v-col>
      </v-row>

      <v-simple-table v-if="!loading && view === 'table'">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Name</th>
              <th class="text-left">Template ID</th>
              <th class="text-left">Property types</th>
              <th class="text-left">Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="template in templates" :key="template.propertyId">
              <td>{{ template.name || "—" }}</td>
              <td>
                <code>{{ template.propertyId }}</code>
              </td>
              <td>
                <v-chip
                  v-for="typeKey in template.propertyTypes"
                  :key="`${template.propertyId}-${typeKey}`"
                  small
                  class="ma-1"
                  color="primary"
                  outlined
                >
                  {{ typeKey }}
                </v-chip>
                <span v-if="!template.propertyTypes.length" class="grey--text">
                  —
                </span>
              </td>
              <td>
                <span v-if="!template.exists" class="error--text">Missing</span>
                <span v-else-if="template.deletedAt" class="warning--text"
                  >Deleted</span
                >
                <span v-else-if="template.publishedAt" class="success--text"
                  >Published</span
                >
                <span v-else class="grey--text">Unpublished</span>
              </td>
              <td class="text-right">
                <v-btn
                  small
                  color="primary"
                  class="mr-2"
                  :disabled="!template.exists || !!template.deletedAt"
                  @click="editTemplate(template)"
                >
                  Edit
                </v-btn>
                <a
                  v-if="template.exists && !template.deletedAt"
                  href="#"
                  class="error--text text-decoration-underline text-body-2"
                  @click.prevent="confirmDeleteTemplate(template)"
                >
                  Delete
                </a>
              </td>
            </tr>
            <tr v-if="!templates.length">
              <td colspan="5" class="text-center grey--text py-6">
                No templates found.
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <p class="text-caption grey--text mt-4 mb-0">
        Templates are properties with <code>isPropertyTemplate</code> (plus the
        default template). Property type comes from each template’s
        <code>type</code> field. Creating or deleting a template updates
        <code>settings.propertyTypeTemplates</code> so setup stays in sync.
      </p>
    </ehc-sheet>

    <v-dialog v-model="createDialog" max-width="520" persistent>
      <v-card class="pa-4">
        <v-card-title class="px-0 pt-0">Create template copy</v-card-title>
        <v-card-subtitle class="px-0">
          Copies an existing property into a new template key. The source
          property is not changed.
        </v-card-subtitle>
        <v-card-text class="px-0">
          <v-text-field
            v-model="createForm.sourcePropertyId"
            label="Source property ID"
            placeholder="e.g. 5093055"
            outlined
            dense
            :disabled="converting"
            hide-details="auto"
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="createForm.templateKey"
            label="New template key"
            placeholder="e.g. hosp_tmpl_br"
            outlined
            dense
            :disabled="converting"
            hint="Used as propertyId, key, and docId (letters, numbers, underscore)"
            persistent-hint
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="createForm.templateName"
            label="Property name"
            placeholder="e.g. Modelo Hospital Brasil"
            outlined
            dense
            :disabled="converting"
            hide-details="auto"
            class="mb-3"
          ></v-text-field>
          <v-select
            v-model="createForm.propertyType"
            :items="propertyTypeOptions"
            item-text="label"
            item-value="value"
            label="Property type"
            placeholder="Select a property type"
            outlined
            dense
            :disabled="converting"
            hint="Stored on the template as type; settings are synced from this page"
            persistent-hint
          ></v-select>
          <v-alert
            v-if="createError"
            type="error"
            dense
            outlined
            class="mt-3 mb-0"
          >
            {{ createError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-0 pb-0">
          <v-spacer></v-spacer>
          <v-btn text :disabled="converting" @click="closeCreateTemplateDialog">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="converting"
            :disabled="!canSubmitCreate"
            @click="createTemplateFromProperty"
          >
            Create template
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ehc-message
      v-model="message"
      @cancel="message = closeMessage"
      @deleteTemplate="deleteTemplateConfirmed()"
      @downloadGuidePdf="downloadGuideAsPdf()"
      @downloadGuideDoc="downloadGuideAsDoc()"
      @cancelPdfDownload="cancelPropertyGuidePdfDownload()"
    >
      <template #qrcode>
        <qrcode :value="message.link" :showTitle="true" :showBigButton="false" />
      </template>
    </ehc-message>
  </ehc-page>
</template>

<script>
import firebase from "firebase";
import apiMixin from "@/mixins/api.vue";
import PropertyActions from "@/mixins/propertyActions.vue";
import EhcPropertyCard from "@/components/properties/ehc-property-card.vue";
import qrcode from "@/components/ehc-qrcode.vue";

const ADDRESS_AND_ORG_DELETES = [
  "mapAddress",
  "address",
  "street",
  "lat",
  "lng",
  "latitude",
  "longitude",
  "coordinates",
  "orgId",
  "orgName",
  "uid",
  "userId",
];

const IDENTITY_KEYS = [
  "propertyId",
  "docId",
  "key",
  "createdAt",
  "updatedAt",
  "copiedAt",
  "copiedFrom",
  "deletedAt",
  "publishedAt",
  "isPropertyTemplate",
  "templateSourceId",
  "templateAppliedAt",
  "updatedFrom",
  "unansweredGuestQuestions",
  "guestAiAssistantAtTop",
];

export default {
  name: "PropertyTemplates",
  components: { EhcPropertyCard, qrcode },
  mixins: [apiMixin, PropertyActions],
  data() {
    return {
      view: "table",
      loading: false,
      converting: false,
      deleting: false,
      error: null,
      createError: null,
      templates: [],
      createDialog: false,
      createForm: {
        sourcePropertyId: "",
        templateKey: "",
        templateName: "",
        propertyType: "",
      },
      propertyTypeOptions: [
        { value: "house", label: "House" },
        { value: "apartment", label: "Apartment" },
        { value: "hotel", label: "Hotel" },
        { value: "resort", label: "Resort" },
        { value: "hostel", label: "Hostel" },
        { value: "camp", label: "Camp/Glamp" },
        { value: "camper", label: "Camper/RV" },
        { value: "tourism", label: "Tourism" },
        { value: "attraction", label: "Attraction" },
        { value: "events", label: "Events" },
        { value: "hospital", label: "Hospital" },
        { value: "hospitalBrazil", label: "Hospital-Brazil" },
        { value: "other", label: "Other" },
        { value: "default", label: "Default" },
      ],
      message: { show: false },
      closeMessage: { show: false },
      pendingDeleteTemplate: null,
      headerActions: [
        {
          actionType: "v-btn",
          label: "Create template",
          color: "primary",
          prependIcon: "mdi-plus",
          clickEmit: "openCreateTemplate",
        },
        {
          actionType: "v-btn",
          label: "Refresh",
          color: "primary",
          prependIcon: "mdi-refresh",
          clickEmit: "refreshTemplates",
        },
      ],
    };
  },
  computed: {
    canSubmitCreate() {
      return (
        !!String(this.createForm.sourcePropertyId || "").trim() &&
        !!String(this.createForm.templateKey || "").trim() &&
        !!String(this.createForm.templateName || "").trim() &&
        !!String(this.createForm.propertyType || "").trim() &&
        !this.converting
      );
    },
    templateCards() {
      // ehc-property-card expects the propertyCardViews shape.
      return this.templates.map((template) => ({
        ...template,
        contactInfo: template.contactInfo || {},
      }));
    },
    cardActions() {
      return [
        {
          type: "link",
          label: "Edit",
          iconURL: require("@/assets/icons/edit@3x.svg"),
          emitClick: "editProperty",
          toolTip: "Edit this template",
        },
        {
          type: "link",
          label: "Sync Card View",
          icon: "mdi-database-sync-outline",
          emitClick: "syncPropertyCardView",
          toolTip: "Update propertyCardViews for this template",
        },
        {
          type: "link",
          label: "Firestore",
          icon: "mdi-database-outline",
          emitClick: "goToFirestoreProperty",
          toolTip: "Open this template in Firebase console",
        },
        {
          type: "link",
          label: "Delete",
          iconURL: require("@/assets/icons/trash@3x.svg"),
          emitClick: "deleteProperty",
          toolTip: "Delete this template",
        },
      ];
    },
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    openCreateTemplateDialog() {
      this.createError = null;
      this.createForm = {
        sourcePropertyId: "",
        templateKey: "",
        templateName: "",
        propertyType: "",
      };
      this.createDialog = true;
    },
    closeCreateTemplateDialog() {
      if (this.converting) {
        return;
      }
      this.createDialog = false;
      this.createError = null;
    },
    defaultPropertyId() {
      return this.$store.getters.defaultPropertyId || "default12821";
    },
    emptyTemplateEntry(propertyId) {
      return {
        propertyId,
        propertyTypes: [],
        name: null,
        type: null,
        exists: false,
        deletedAt: null,
        publishedAt: null,
        key: propertyId,
        docId: propertyId,
      };
    },
    addPropertyTypeToEntry(entry, typeValue) {
      const typeKey = String(typeValue || "").trim();
      if (!typeKey || !entry) {
        return;
      }
      entry.type = typeKey;
      entry.propertyTypes = [typeKey];
    },
    reverseSettingsTypeMap() {
      const map = {};
      const settingsMap =
        this.$store.getters.appSettings?.propertyTypeTemplates || {};
      Object.entries(settingsMap).forEach(([typeKey, rawId]) => {
        const propertyId = String(rawId || "").trim();
        if (propertyId && !map[propertyId]) {
          map[propertyId] = typeKey;
        }
      });
      return map;
    },
    entryFromPropertyDoc(propertyId, data = {}) {
      const entry = this.emptyTemplateEntry(propertyId);
      entry.exists = true;
      entry.name = data.name || propertyId;
      entry.deletedAt = data.deletedAt ?? null;
      entry.publishedAt = data.publishedAt ?? null;
      entry.avatarImg = data.avatarImg || null;
      entry.isPropertyTemplate = data.isPropertyTemplate === true;
      entry.createdAt = data.createdAt ?? null;
      entry.city = data.city || "";
      entry.state = data.state || "";
      entry.showName = data.showName;
      entry.showAddress = data.showAddress;

      let typeKey = String(data.type || "").trim();
      if (!typeKey) {
        typeKey = this.reverseSettingsTypeMap()[propertyId] || "";
      }
      if (!typeKey && propertyId === this.defaultPropertyId()) {
        typeKey = "default";
      }
      if (typeKey) {
        this.addPropertyTypeToEntry(entry, typeKey);
      }
      return entry;
    },
    async ensureSettings() {
      if (!this.$store.getters.appSettings) {
        await this.apiGetAppSettings();
      }
    },
    async loadTemplates() {
      this.loading = true;
      this.error = null;
      try {
        await this.ensureSettings();
        const byId = new Map();
        const defaultId = this.defaultPropertyId();

        const flaggedSnap = await firebase
          .firestore()
          .collection("properties")
          .where("isPropertyTemplate", "==", true)
          .get();

        flaggedSnap.forEach((doc) => {
          const data = doc.data() || {};
          if (data.deletedAt != null) {
            return;
          }
          byId.set(doc.id, this.entryFromPropertyDoc(doc.id, data));
        });

        // Always include the default template, even if not flagged yet.
        if (!byId.has(defaultId)) {
          const data = await this.apiGetDoc("properties", defaultId);
          if (data && data.deletedAt == null) {
            byId.set(defaultId, this.entryFromPropertyDoc(defaultId, data));
          } else if (!data) {
            const missing = this.emptyTemplateEntry(defaultId);
            this.addPropertyTypeToEntry(missing, "default");
            byId.set(defaultId, missing);
          }
        } else {
          const defaultEntry = byId.get(defaultId);
          if (!defaultEntry.type) {
            this.addPropertyTypeToEntry(defaultEntry, "default");
          }
        }

        // Heal missing type on template docs from older settings mappings.
        await Promise.all(
          [...byId.values()].map(async (entry) => {
            if (!entry.exists || entry.deletedAt != null || entry.type) {
              return;
            }
            const healedType = this.reverseSettingsTypeMap()[entry.propertyId];
            if (!healedType) {
              return;
            }
            this.addPropertyTypeToEntry(entry, healedType);
            try {
              await firebase
                .firestore()
                .collection("properties")
                .doc(entry.propertyId)
                .set({ type: healedType }, { merge: true });
            } catch (error) {
              console.warn(
                "Failed to backfill template type",
                entry.propertyId,
                error,
              );
            }
          }),
        );

        const rows = [...byId.values()].filter((row) => row.deletedAt == null);
        rows.sort((a, b) => {
          const aDefault = a.propertyTypes.includes("default") ? 0 : 1;
          const bDefault = b.propertyTypes.includes("default") ? 0 : 1;
          if (aDefault !== bDefault) {
            return aDefault - bDefault;
          }
          return String(a.name || a.propertyId).localeCompare(
            String(b.name || b.propertyId),
          );
        });

        this.templates = rows;
        await this.syncSettingsFromTemplates(rows);
      } catch (error) {
        this.error = error?.message || "Failed to load templates";
        this.templates = [];
      } finally {
        this.loading = false;
      }
    },
    buildSettingsMapFromTemplates(templates) {
      const next = {
        default: this.defaultPropertyId(),
      };
      (templates || []).forEach((template) => {
        const typeKey = String(template.type || "").trim();
        const propertyId = String(template.propertyId || "").trim();
        if (!typeKey || !propertyId || template.deletedAt != null) {
          return;
        }
        // Keep default reserved for the default template id.
        if (typeKey === "default") {
          next.default = this.defaultPropertyId();
          return;
        }
        next[typeKey] = propertyId;
      });
      return next;
    },
    settingsMapsEqual(a = {}, b = {}) {
      const aKeys = Object.keys(a).sort();
      const bKeys = Object.keys(b).sort();
      if (aKeys.length !== bKeys.length) {
        return false;
      }
      return aKeys.every(
        (key, index) =>
          key === bKeys[index] && String(a[key] || "") === String(b[key] || ""),
      );
    },
    async syncSettingsFromTemplates(templates) {
      await this.ensureSettings();
      const next = this.buildSettingsMapFromTemplates(templates);
      const current =
        this.$store.getters.appSettings?.propertyTypeTemplates || {};
      if (this.settingsMapsEqual(current, next)) {
        return;
      }
      await this.apiUpdateDoc("settings", "globalAppSettings", {
        propertyTypeTemplates: next,
      });
      await this.apiGetAppSettings();
    },
    normalizeTemplateKey(value) {
      return String(value || "").trim();
    },
    validateTemplateKey(key) {
      if (!/^[a-zA-Z0-9_]{1,32}$/.test(key)) {
        throw new Error(
          "Template key must be 1–32 characters: letters, numbers, underscore only.",
        );
      }
    },
    stripIdentityAndMeta(data) {
      const next = { ...(data || {}) };
      IDENTITY_KEYS.forEach((key) => {
        delete next[key];
      });
      ADDRESS_AND_ORG_DELETES.forEach((key) => {
        delete next[key];
      });
      Object.keys(next).forEach((key) => {
        if (key.startsWith("unansweredGuestQuestions")) {
          delete next[key];
        }
      });
      return next;
    },
    buildTemplateDoc(
      sourcePropertyId,
      templateKey,
      propertyType,
      templateName,
      sourceData,
    ) {
      const base = this.stripIdentityAndMeta(sourceData);
      const name = String(templateName || "").trim();
      const now = Date.now();
      return {
        ...base,
        propertyId: templateKey,
        docId: templateKey,
        key: templateKey,
        name,
        type: propertyType,
        isPropertyTemplate: true,
        templateSourceId: sourcePropertyId,
        copiedFrom: sourcePropertyId,
        uid: null,
        userId: null,
        orgId: null,
        orgName: null,
        city: "",
        state: "",
        zip: "",
        publishedAt: null,
        deletedAt: null,
        contactInfo: {
          hostName: null,
          hostPhotoUrl: null,
          arbnbBool: false,
          vrboBool: false,
          homeAwayBool: false,
          bookingBool: false,
          callingNumber: null,
          smsNumber: null,
        },
        createdAt: now,
        updatedAt: now,
        updatedFrom: "admin-templates-page",
      };
    },
    async copySubcollections(sourcePropertyId, templateKey) {
      const db = firebase.firestore();
      const sourceRef = db.collection("properties").doc(sourcePropertyId);
      const targetRef = db.collection("properties").doc(templateKey);

      for (const collectionName of ["propertyinfo", "areaguide"]) {
        const snap = await sourceRef
          .collection(collectionName)
          .doc("subsections")
          .get();
        if (snap.exists) {
          await targetRef
            .collection(collectionName)
            .doc("subsections")
            .set(snap.data() || {});
        }
      }

      const cardsSnap = await sourceRef.collection("customCardItems").get();
      if (!cardsSnap.empty) {
        const batch = db.batch();
        cardsSnap.forEach((doc) => {
          batch.set(
            targetRef.collection("customCardItems").doc(doc.id),
            doc.data() || {},
          );
        });
        await batch.commit();
      }
    },
    async syncCardViewAfterTemplateConvert(propertyId, propData) {
      const viewRef = firebase
        .firestore()
        .collection("propertyCardViews")
        .doc(propertyId);
      const payload = {
        propertyId,
        key: propertyId,
        docId: propertyId,
        orgId: null,
        uid: null,
        name: propData.name || "",
        avatarImg: propData.avatarImg ?? null,
        city: "",
        state: "",
        zip: "",
        createdAt: propData.createdAt ?? null,
        updatedAt: Date.now(),
        publishedAt: propData.publishedAt ?? null,
        showName: propData.showName,
        showAddress: propData.showAddress,
        noguestinfo: propData.noguestinfo,
        isPropertyTemplate: true,
        syncedAt: firebase.firestore.Timestamp.now(),
        contactInfo: { hostPhotoUrl: null },
      };
      await viewRef.set(payload);
    },
    async createTemplateFromProperty() {
      const sourcePropertyId = String(
        this.createForm.sourcePropertyId || "",
      ).trim();
      const templateKey = this.normalizeTemplateKey(this.createForm.templateKey);
      const templateName = String(this.createForm.templateName || "").trim();
      const propertyType = String(this.createForm.propertyType || "").trim();

      this.createError = null;
      this.error = null;

      if (!sourcePropertyId || !templateKey || !templateName || !propertyType) {
        this.createError =
          "Source property ID, template key, property name, and property type are required.";
        return;
      }

      this.converting = true;
      try {
        this.validateTemplateKey(templateKey);

        if (sourcePropertyId === templateKey) {
          throw new Error(
            "Template key must be different from the source property ID.",
          );
        }

        const sourceSnap = await firebase
          .firestore()
          .collection("properties")
          .doc(sourcePropertyId)
          .get();
        if (!sourceSnap.exists) {
          throw new Error(`Property ${sourcePropertyId} not found.`);
        }

        const sourceData = sourceSnap.data() || {};
        if (sourceData.deletedAt != null) {
          throw new Error(
            `Property ${sourcePropertyId} is deleted. Undelete it before copying.`,
          );
        }

        const existingTarget = await firebase
          .firestore()
          .collection("properties")
          .doc(templateKey)
          .get();
        if (existingTarget.exists) {
          throw new Error(
            `Template key ${templateKey} already exists. Choose a different key.`,
          );
        }

        const templateDoc = this.buildTemplateDoc(
          sourcePropertyId,
          templateKey,
          propertyType,
          templateName,
          sourceData,
        );

        await firebase
          .firestore()
          .collection("properties")
          .doc(templateKey)
          .set(templateDoc);

        await this.copySubcollections(sourcePropertyId, templateKey);
        await this.syncCardViewAfterTemplateConvert(templateKey, templateDoc);

        this.createDialog = false;
        this.createForm = {
          sourcePropertyId: "",
          templateKey: "",
          templateName: "",
          propertyType: "",
        };
        this.$store.commit("setAlert", {
          type: "success",
          text: `Created template ${templateKey} from ${sourcePropertyId} for type ${propertyType}. Source property was not changed.`,
        });
        await this.loadTemplates();
      } catch (error) {
        this.createError =
          error?.message ||
          error?.details ||
          "Failed to create template from property";
      } finally {
        this.converting = false;
      }
    },
    editTemplate(template) {
      this.openPropertyEditor({
        propertyId: template.propertyId,
        key: template.propertyId,
        docId: template.propertyId,
      });
    },
    templateLink(template) {
      return `${this.$store.getters.appSite}${template.propertyId}`;
    },
    showTemplateLink(template) {
      const link = this.templateLink(template);
      navigator.clipboard.writeText(link);
      this.message = {
        show: true,
        width: 420,
        title: "Link copied",
        message: link,
        actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
      };
    },
    showTemplateQRCode(template) {
      this.message = {
        show: true,
        width: 450,
        title: "Template QR Code",
        link: this.templateLink(template),
        slotName: "qrcode",
        actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
      };
    },
    async publishTemplate(template) {
      await this.publishProperty(template);
      await this.loadTemplates();
    },
    confirmDeleteTemplate(template) {
      const types = (template.propertyTypes || []).join(", ");
      const wiredWarning = types
        ? ` This template is used for type(s): ${types}. Settings will be rebuilt from remaining templates after delete.`
        : "";
      this.pendingDeleteTemplate = template;
      this.message = {
        show: true,
        persistent: true,
        width: 520,
        title: "Delete this template?",
        alert: {
          type: "error",
          text: "Strong warning: deleting a template can break guidebook setup and guest links that depend on it.",
        },
        message: `Delete "${template.name || template.propertyId}" (${template.propertyId})? This cannot be undone from this page.${wiredWarning}`,
        actions: [
          {
            label: "Yes, delete template",
            clickEmit: "deleteTemplate",
            color: "error",
          },
          { type: "spacer" },
          { label: "Cancel", clickEmit: "cancel", plain: true },
        ],
      };
    },
    async deleteTemplateConfirmed() {
      const template = this.pendingDeleteTemplate;
      this.pendingDeleteTemplate = null;
      this.message = {
        show: true,
        persistent: true,
        width: 360,
        loading: true,
        title: "Deleting template",
        message: "Deleting...",
      };

      if (!template?.propertyId) {
        this.message = this.closeMessage;
        return;
      }

      this.deleting = true;
      try {
        // Templates have no orgId (cleared on create). The setPropertyAsDeleted
        // callable always decrements org propCount and throws INTERNAL without one.
        const deletedAt = Date.now();
        await firebase
          .firestore()
          .collection("properties")
          .doc(template.propertyId)
          .set({ deletedAt }, { merge: true });
        this.$store.commit("setAlert", {
          type: "success",
          text: `Soft-deleted template ${template.propertyId}.`,
        });
        this.message = this.closeMessage;
        await this.loadTemplates();
      } catch (error) {
        this.error = error?.message || "Failed to delete template";
        this.message = {
          show: true,
          width: 420,
          title: "Delete failed",
          message: this.error,
          actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
        };
        this.$store.commit("setAlert", {
          type: "error",
          text: this.error,
        });
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
